/**
 * Webhook Logs Component
 * Display webhook delivery logs with filtering
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface WebhookLog {
  id: number;
  webhookConfigId: number;
  eventType: string;
  status: string;
  payload: string;
  response: string;
  errorMessage?: string;
  httpStatus?: number;
  retryCount: number;
  nextRetryAt?: string;
  createdAt: string;
}

interface WebhookLogsProps {
  webhookId?: number;
}

export const WebhookLogs: React.FC<WebhookLogsProps> = ({ webhookId }) => {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterEventType, setFilterEventType] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalCount: 0,
    totalPages: 0,
  });
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);

  useEffect(() => {
    loadLogs();
  }, [webhookId, filterStatus, filterEventType, page]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });

      if (webhookId) {
        params.append('webhookId', webhookId.toString());
      }
      if (filterStatus) {
        params.append('status', filterStatus);
      }
      if (filterEventType) {
        params.append('eventType', filterEventType);
      }

      const response = await apiClient.get(
        `/api/v1/admin/webhooks/logs?${params.toString()}`
      );

      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || pagination);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (logId: number) => {
    try {
      await apiClient.post(`/api/v1/admin/webhooks/logs/${logId}/retry`);
      await loadLogs();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to retry webhook');
    }
  };

  const toggleExpanded = (logId: number) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      success: 'bg-green-900/30 text-green-400 border-green-500',
      failed: 'bg-red-900/30 text-red-400 border-red-500',
      retrying: 'bg-yellow-900/30 text-yellow-400 border-yellow-500',
    };

    return (
      <span
        className={`px-2 py-1 rounded-lg text-xs font-medium border ${
          styles[status as keyof typeof styles] || 'bg-slate-700/50 text-slate-400 border-slate-600/50'
        }`}
      >
        {status.toUpperCase()}
      </span>
    );
  };

  const getEventTypeColor = (eventType: string): string => {
    if (eventType.startsWith('payment.')) return 'text-green-400';
    if (eventType.startsWith('invoice.')) return 'text-orange-400';
    if (eventType.startsWith('order.')) return 'text-purple-400';
    if (eventType.startsWith('service.')) return 'text-yellow-400';
    if (eventType.startsWith('ticket.')) return 'text-orange-400';
    if (eventType.startsWith('domain.')) return 'text-indigo-400';
    if (eventType.startsWith('affiliate.')) return 'text-pink-400';
    if (eventType.startsWith('client.')) return 'text-cyan-400';
    return 'text-slate-400';
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading webhook logs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-slate-800/80 rounded-lg p-4 border border-slate-700/50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="retrying">Retrying</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Filter by Event Type
            </label>
            <input
              type="text"
              value={filterEventType}
              onChange={(e) => {
                setFilterEventType(e.target.value);
                setPage(1);
              }}
              placeholder="e.g., payment.received"
              className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="space-y-3">
        {logs.length === 0 ? (
          <div className="bg-slate-800/80 rounded-lg p-8 text-center border border-slate-700/50">
            <div className="text-slate-400 text-lg">No logs found</div>
            <p className="text-slate-500 mt-2">
              {filterStatus || filterEventType
                ? 'Try adjusting your filters'
                : 'Webhook delivery logs will appear here'}
            </p>
          </div>
        ) : (
          logs.map((log) => (
            <div
              key={log.id}
              className="bg-slate-800/80 rounded-lg border border-slate-700/50 hover:border-slate-600/50 transition-colors"
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      {getStatusBadge(log.status)}
                      <span
                        className={`text-sm font-medium ${getEventTypeColor(
                          log.eventType
                        )}`}
                      >
                        {log.eventType}
                      </span>
                      {log.httpStatus && (
                        <span className="text-sm text-slate-400">
                          HTTP {log.httpStatus}
                        </span>
                      )}
                      {log.retryCount > 0 && (
                        <span className="text-sm text-yellow-400">
                          Retry #{log.retryCount}
                        </span>
                      )}
                    </div>

                    <div className="mt-2 text-sm text-slate-400">
                      {new Date(log.createdAt).toLocaleString()}
                    </div>

                    {log.errorMessage && (
                      <div className="mt-2 text-sm text-red-400 bg-red-900/20 px-3 py-2 rounded">
                        Error: {log.errorMessage}
                      </div>
                    )}

                    {log.status === 'retrying' && log.nextRetryAt && (
                      <div className="mt-2 text-sm text-yellow-400">
                        Next retry:{' '}
                        {new Date(log.nextRetryAt).toLocaleString()}
                      </div>
                    )}

                    {/* Expanded Details */}
                    {expandedLogId === log.id && (
                      <div className="mt-4 space-y-3">
                        {log.payload && (
                          <div>
                            <div className="text-sm font-medium text-slate-300 mb-1">
                              Payload:
                            </div>
                            <pre className="bg-slate-900 p-3 rounded text-xs text-slate-300 overflow-x-auto">
                              {JSON.stringify(JSON.parse(log.payload), null, 2)}
                            </pre>
                          </div>
                        )}

                        {log.response && (
                          <div>
                            <div className="text-sm font-medium text-slate-300 mb-1">
                              Response:
                            </div>
                            <pre className="bg-slate-900 p-3 rounded text-xs text-slate-300 overflow-x-auto">
                              {log.response}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => toggleExpanded(log.id)}
                      className="px-3 py-1 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {expandedLogId === log.id ? 'Hide' : 'Details'}
                    </button>

                    {log.status === 'failed' && (
                      <button
                        onClick={() => handleRetry(log.id)}
                        className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between bg-slate-800/80 rounded-lg p-4 border border-slate-700/50">
          <div className="text-sm text-slate-400">
            Showing {(page - 1) * pagination.limit + 1} to{' '}
            {Math.min(page * pagination.limit, pagination.totalCount)} of{' '}
            {pagination.totalCount} logs
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <div className="flex items-center space-x-2">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum: number;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                      page === pageNum
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-700/50 hover:bg-slate-600/50 text-white'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
